import React, { useEffect, useMemo, useState } from "react";
import { logError } from "../lib/log";
import { api } from "../lib/api";
import { X } from "lucide-react";

// Group and period placement rules for a standard 18-column periodic table.
// For lanthanides (57-71) and actinides (89-103), we render a separate block below.

function categoryClass(cat) {
  const map = {
    alkali: "el-alkali",
    alkaline: "el-alkaline",
    transition: "el-transition",
    "post-transition": "el-post-transition",
    metalloid: "el-metalloid",
    nonmetal: "el-nonmetal",
    halogen: "el-halogen",
    noble: "el-noble",
    lanthanide: "el-lanthanide",
    actinide: "el-actinide",
  };
  return map[cat] || "el-nonmetal";
}

const CATEGORY_LEGEND = [
  ["alkali", "Alkali"],
  ["alkaline", "Alkaline"],
  ["transition", "Transition"],
  ["post-transition", "Post-transition"],
  ["metalloid", "Metalloid"],
  ["nonmetal", "Nonmetal"],
  ["halogen", "Halogen"],
  ["noble", "Noble gas"],
  ["lanthanide", "Lanthanide"],
  ["actinide", "Actinide"],
];

function ElementCell({ el, onClick }) {
  return (
    <button
      onClick={() => onClick(el)}
      data-testid={`element-${el.symbol}-cell`}
      className={`element-cell aspect-square rounded-sm border p-1 text-left ${categoryClass(el.category)}`}
      style={{ gridColumn: el.group, gridRow: el.period }}
    >
      <div className="font-mono-num text-[8px] leading-none opacity-80 sm:text-[10px]">{el.number}</div>
      <div className="font-display mt-0.5 text-sm font-bold sm:text-lg">{el.symbol}</div>
      <div className="mt-0.5 hidden text-[8px] leading-tight opacity-80 sm:block sm:text-[9px]">{el.name}</div>
    </button>
  );
}

export default function PeriodicTable() {
  const [elements, setElements] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/elements").then((r) => setElements(r.data.elements)).catch((e) => logError("Elements load failed", e));
  }, []);

  const { mainGrid, lanthanides, actinides } = useMemo(() => {
    const main = [];
    const la = [];
    const ac = [];
    for (const el of elements) {
      if (el.category === "lanthanide") la.push(el);
      else if (el.category === "actinide") ac.push(el);
      else main.push(el);
    }
    // Lanthanum(57) and Actinium(89) are placed at group 3 rows in main but we render them below too for clarity.
    return { mainGrid: main, lanthanides: la, actinides: ac };
  }, [elements]);

  const filteredCellCls = (cat) => (filter === "all" || filter === cat ? "" : "opacity-20");

  return (
    <div className="space-y-8" data-testid="periodic-table-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">Periodic Table</h1>
          <p className="mt-2 text-sm text-slate-400">Tap any element for details.</p>
        </div>
        <div className="scroll-x-snap -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0" data-testid="category-filter">
          <button
            onClick={() => setFilter("all")}
            data-testid="filter-all"
            className={`shrink-0 whitespace-nowrap rounded-md border px-3 py-1 text-xs font-medium transition-colors ${
              filter === "all" ? "border-cyan-400/50 bg-cyan-500/10 text-cyan-300" : "border-white/10 text-slate-400 hover:text-slate-100"
            }`}
          >
            All
          </button>
          {CATEGORY_LEGEND.map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`filter-${k}`}
              className={`element-cell shrink-0 whitespace-nowrap rounded-md border px-3 py-1 text-xs font-medium ${categoryClass(k)} ${
                filter === k ? "" : "opacity-70"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Main table */}
      <div className="relative rounded-md border border-white/10 bg-black/40 p-3 sm:p-5">
        <div className="pointer-events-none absolute right-2 top-2 z-10 rounded-full bg-cyan-500/15 px-2 py-0.5 text-[10px] font-medium text-cyan-300 sm:hidden" data-testid="pt-scroll-hint">
          swipe →
        </div>
        <div className="overflow-x-auto">
        <div
          className="grid gap-1 sm:gap-1.5"
          style={{ gridTemplateColumns: "repeat(18, minmax(38px, 1fr))", minWidth: 700 }}
          data-testid="main-grid"
        >
          {mainGrid.map((el) => (
            <div key={el.number} className={filteredCellCls(el.category)} style={{ gridColumn: el.group, gridRow: el.period }}>
              <ElementCell el={el} onClick={setSelected} />
            </div>
          ))}

          {/* Placeholder cells for lanthanide/actinide slot indicators */}
          <div
            className="flex items-center justify-center rounded-sm border border-fuchsia-400/30 bg-fuchsia-500/5 text-[10px] text-fuchsia-300 el-lanthanide"
            style={{ gridColumn: 3, gridRow: 6 }}
          >
            57-71
          </div>
          <div
            className="flex items-center justify-center rounded-sm border border-orange-400/30 bg-orange-500/5 text-[10px] text-orange-300 el-actinide"
            style={{ gridColumn: 3, gridRow: 7 }}
          >
            89-103
          </div>
        </div>

        {/* Lanthanides and actinides rows */}
        <div className="mt-6 space-y-2">
          <div className="grid gap-1 sm:gap-1.5" style={{ gridTemplateColumns: "repeat(15, minmax(38px, 1fr))", minWidth: 700 }}>
            {lanthanides.map((el, i) => (
              <div key={el.number} className={filteredCellCls(el.category)}>
                <button
                  onClick={() => setSelected(el)}
                  data-testid={`element-${el.symbol}-cell`}
                  className={`element-cell aspect-square w-full rounded-sm border p-1 text-left ${categoryClass(el.category)}`}
                >
                  <div className="font-mono-num text-[8px] leading-none opacity-80 sm:text-[10px]">{el.number}</div>
                  <div className="font-display mt-0.5 text-sm font-bold sm:text-lg">{el.symbol}</div>
                  <div className="mt-0.5 hidden text-[8px] leading-tight opacity-80 sm:block sm:text-[9px]">{el.name}</div>
                </button>
              </div>
            ))}
          </div>
          <div className="grid gap-1 sm:gap-1.5" style={{ gridTemplateColumns: "repeat(15, minmax(38px, 1fr))", minWidth: 700 }}>
            {actinides.map((el) => (
              <div key={el.number} className={filteredCellCls(el.category)}>
                <button
                  onClick={() => setSelected(el)}
                  data-testid={`element-${el.symbol}-cell`}
                  className={`element-cell aspect-square w-full rounded-sm border p-1 text-left ${categoryClass(el.category)}`}
                >
                  <div className="font-mono-num text-[8px] leading-none opacity-80 sm:text-[10px]">{el.number}</div>
                  <div className="font-display mt-0.5 text-sm font-bold sm:text-lg">{el.symbol}</div>
                  <div className="mt-0.5 hidden text-[8px] leading-tight opacity-80 sm:block sm:text-[9px]">{el.name}</div>
                </button>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>

      {/* Element detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center" onClick={() => setSelected(null)}>
          <div
            className={`max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-white/10 bg-[#0b0b10] p-5 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] sm:rounded-md sm:p-6 ${categoryClass(selected.category)}`}
            onClick={(e) => e.stopPropagation()}
            data-testid="element-detail"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono-num text-xs uppercase tracking-widest opacity-80">#{selected.number} · {selected.category}</div>
                <div className="font-display mt-2 text-5xl font-bold sm:text-6xl">{selected.symbol}</div>
                <div className="mt-1 text-2xl font-semibold text-white">{selected.name}</div>
              </div>
              <button onClick={() => setSelected(null)} data-testid="close-element-detail" className="icon-btn rounded-md p-2 text-slate-400 hover:bg-white/10">
                <X size={18} />
              </button>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-md border border-white/10 p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Atomic mass</div>
                <div className="font-mono-num mt-1 text-white">{selected.mass}</div>
              </div>
              <div className="rounded-md border border-white/10 p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Group / Period</div>
                <div className="font-mono-num mt-1 text-white">{selected.group} / {selected.period}</div>
              </div>
              <div className="col-span-2 rounded-md border border-white/10 p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Electron configuration</div>
                <div className="font-mono-num mt-1 text-white">{selected.electron_config}</div>
              </div>
            </div>

            <p className="mt-4 text-sm leading-relaxed text-slate-300">{selected.description}</p>
          </div>
        </div>
      )}
    </div>
  );
}
